import "dayjs/locale/de";
import "dayjs/locale/en";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { deDE, enUS } from "@mui/x-date-pickers/locales";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import { ReactNode, useCallback, useMemo, useState } from "react";

import { checkAppNameAvailable, FrequencyUnit } from "../helpers/fetch-apps";

export interface CreateAppPayload {
  name: string;
  description: string;
  programOwner?: string;
  useCase: string;
  reportingPeriod: {
    startDate: string;
    endDate: string;
  };
  frequency: {
    unit: FrequencyUnit;
    interval: number;
    weeklyDay?: string;
    monthlyDay?: number;
    yearlyDate?: string;
  };
  /**
   * Active Home Portal UI language at submission time. Forwarded all the way
   * to test-backend, where it is persisted as `configuration.defaultLanguage`
   * and used to seed localized sample data.
   */
  language: SupportedLanguage;
}

type CreateAppFormState = {
  name: string;
  description: string;
  programOwner: string;
  useCase: string;
  startDate: string;
  endDate: string;
  frequencyUnit: FrequencyUnit;
  frequencyInterval: number;
  weeklyDay?: string;
  monthlyDay?: number;
  yearlyDate?: string;
};

export interface CreateAppModalState {
  isOpen: boolean;
  isCreatingApp?: boolean;
  successMessage?: string;
  selectedClientId?: string;
  language: SupportedLanguage;
}

export type CreateAppModalEvent = "create-app" | "close-modal";

type UseCaseOption = {
  id: string;
  title: string;
  description: string;
  icon: ReactNode;
  disabled?: boolean;
};

type TouchedState = {
  name?: boolean;
  programOwner?: boolean;
  startDate?: boolean;
  endDate?: boolean;
};

const useCaseOptions: UseCaseOption[] = [
  {
    id: "pmo",
    title: "createAppModal.screen1.useCases.pmo.title",
    description: "createAppModal.screen1.useCases.pmo.description",
    icon: (
      <Box display="flex" gap={1}>
        {["#2ecc71", "#f1c40f", "#e67e22"].map((color) => (
          <Box
            key={color}
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: color,
            }}
          />
        ))}
      </Box>
    ),
  },
  {
    id: "rollout",
    title: "createAppModal.screen1.useCases.rollout.title",
    description: "createAppModal.screen1.useCases.rollout.description",
    icon: <CheckCircleOutlineIcon sx={{ color: "#12b76a", fontSize: 40 }} />,
  },
  {
    id: "audit",
    title: "createAppModal.screen1.useCases.audit.title",
    description: "createAppModal.screen1.useCases.audit.description",
    icon: <RemoveRedEyeOutlinedIcon sx={{ color: "#0ea5e9", fontSize: 40 }} />,
  },
  {
    id: "maturity",
    title: "createAppModal.screen1.useCases.maturity.title",
    description: "createAppModal.screen1.useCases.maturity.description",
    icon: <InsightsOutlinedIcon sx={{ color: "#f59e0b", fontSize: 40 }} />,
    disabled: true,
  },
];

const steps = ["Use Case", "Name", "Period", "Frequency"];

const dayOptions = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const initialFormState: CreateAppFormState = {
  name: "",
  description: "",
  programOwner: "",
  useCase: "",
  startDate: "",
  endDate: "",
  frequencyUnit: "weekly",
  frequencyInterval: 1,
  weeklyDay: undefined,
  monthlyDay: 1,
  yearlyDate: "",
};

const PROGRAM_OWNER_MAX_LENGTH = 30;

const PRIMARY_DARK = "#1f2937";
const ACTIVE_BG = "#111827";

const FrequencyButton = ({
  label,
  isActive,
  onClick,
  width = 150,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  width?: number;
}) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      width,
      px: 4,
      py: 2,
      borderRadius: "15px",
      border: "1px solid",
      borderColor: PRIMARY_DARK,
      color: isActive ? "#ffffff" : PRIMARY_DARK,
      backgroundColor: isActive ? ACTIVE_BG : "#ffffff",
      "&:hover": {
        color: PRIMARY_DARK,
        backgroundColor: "#f5f5f5",
      },
      "&:focus": {
        color: "#ffffff",
        backgroundColor: ACTIVE_BG,
      },
      fontWeight: 500,
      lineHeight: "20px",
      fontSize: "14px",
      textTransform: "none",
      textAlign: "center",
    }}
  >
    {label}
  </ButtonBase>
);

export const CreateAppModal: PimoReactComponent<
  CreateAppModalState,
  CreateAppModalEvent,
  CreateAppPayload
> = ({
  fireEvent,
  isOpen = false,
  isCreatingApp = false,
  successMessage,
  selectedClientId,
  language,
}) => {
  const theme = useTheme();
  const [formData, setFormData] =
    useState<CreateAppFormState>(initialFormState);
  const [activeStep, setActiveStep] = useState(0);
  const [touchedFields, setTouchedFields] = useState<TouchedState>({});
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);

  const useCaseMap = useMemo(
    () =>
      useCaseOptions.reduce<Record<string, UseCaseOption>>((acc, option) => {
        acc[option.id] = option;
        return acc;
      }, {}),
    []
  );

  const resetForm = () => {
    setFormData(initialFormState);
    setActiveStep(0);
    setTouchedFields({});
    setNameAvailable(null);
  };

  const handleNameBlur = useCallback(async () => {
    const name = formData.name.trim();
    if (
      !name ||
      !/^[a-zA-Z0-9\s]+$/.test(name) ||
      !selectedClientId ||
      isCheckingName
    ) {
      return;
    }
    setIsCheckingName(true);
    try {
      const { available } = await checkAppNameAvailable(selectedClientId, name);
      setNameAvailable(available);
    } finally {
      setIsCheckingName(false);
    }
  }, [formData.name, selectedClientId, isCheckingName]);

  const handleClose = () => {
    if (isCreatingApp) return;
    resetForm();
    fireEvent?.("close-modal");
  };

  const markTouched = (field: keyof TouchedState) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleFrequencyUnitChange = (unit: FrequencyUnit) => {
    setFormData((prev) => ({
      ...prev,
      frequencyUnit: unit,
      frequencyInterval: 1,
      weeklyDay: unit === "weekly" ? prev.weeklyDay : undefined,
      monthlyDay: unit === "monthly" ? (prev.monthlyDay ?? 1) : undefined,
      yearlyDate: unit === "yearly" ? (prev.yearlyDate ?? "") : "",
    }));
  };

  const handleWeeklyDaySelect = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      weeklyDay: prev.weeklyDay === day ? undefined : day,
    }));
  };

  const isReportingPeriodValid =
    Boolean(formData.startDate) &&
    Boolean(formData.endDate) &&
    dayjs(formData.endDate).isAfter(dayjs(formData.startDate));

  const isFrequencyValid = () => {
    if (formData.frequencyInterval < 1) {
      return false;
    }

    if (formData.frequencyUnit === "weekly") {
      return Boolean(formData.weeklyDay);
    }

    if (formData.frequencyUnit === "monthly") {
      return Boolean(
        formData.monthlyDay &&
        formData.monthlyDay >= 1 &&
        formData.monthlyDay <= 31
      );
    }

    return Boolean(formData.yearlyDate);
  };

  const isCurrentStepValid = (() => {
    switch (activeStep) {
      case 0:
        return Boolean(formData.useCase);
      case 1:
        return (
          Boolean(formData.name.trim() && formData.programOwner.trim()) &&
          formData.programOwner.trim().length <= PROGRAM_OWNER_MAX_LENGTH &&
          /^[a-zA-Z0-9\s]+$/.test(formData.name.trim()) &&
          nameAvailable !== false
        );
      case 2:
        return isReportingPeriodValid;
      case 3:
        return isFrequencyValid();
      default:
        return true;
    }
  })();

  const handleSubmit = () => {
    if (isCreatingApp) return;
    const payload: CreateAppPayload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      programOwner: formData.programOwner.trim(),
      useCase: formData.useCase,
      reportingPeriod: {
        startDate: formData.startDate,
        endDate: formData.endDate,
      },
      frequency: {
        unit: formData.frequencyUnit,
        interval: formData.frequencyInterval,
        weeklyDay:
          formData.frequencyUnit === "weekly" ? formData.weeklyDay : undefined,
        monthlyDay:
          formData.frequencyUnit === "monthly"
            ? formData.monthlyDay
            : undefined,
        yearlyDate:
          formData.frequencyUnit === "yearly" ? formData.yearlyDate : undefined,
      },
      language,
    };
    fireEvent?.("create-app", payload);
  };

  const nameError = (() => {
    if (activeStep !== 1) return undefined;
    if (!formData.name.trim()) {
      if (!touchedFields.name) return undefined;
      return getLocalizedMessage(
        language,
        "createAppModal.validation.name.required"
      );
    }
    if (!/^[a-zA-Z0-9\s]+$/.test(formData.name.trim())) {
      return getLocalizedMessage(
        language,
        "createAppModal.validation.name.invalidCharacters"
      );
    }
    if (nameAvailable === false) {
      return getLocalizedMessage(
        language,
        "createAppModal.validation.name.alreadyExists"
      );
    }
    return undefined;
  })();

  const programOwnerError = (() => {
    if (activeStep !== 1) return undefined;
    if (!touchedFields.programOwner) return undefined;

    const trimmedValue = formData.programOwner.trim();
    if (!trimmedValue) {
      return getLocalizedMessage(
        language,
        "createAppModal.validation.programOwner.minLength"
      );
    }
    if (trimmedValue.length > PROGRAM_OWNER_MAX_LENGTH) {
      return getLocalizedMessage(
        language,
        "createAppModal.validation.programOwner.maxLength"
      );
    }
    return undefined;
  })();

  const startDateError =
    touchedFields.startDate && !formData.startDate
      ? getLocalizedMessage(
          language,
          "createAppModal.validation.reportingPeriod.startDate.required"
        )
      : undefined;

  const endDateError = (() => {
    if (!touchedFields.endDate) return undefined;
    if (!formData.endDate) {
      return getLocalizedMessage(
        language,
        "createAppModal.validation.reportingPeriod.endDate.required"
      );
    }
    if (
      formData.startDate &&
      !dayjs(formData.endDate).isAfter(dayjs(formData.startDate))
    ) {
      return getLocalizedMessage(
        language,
        "createAppModal.validation.reportingPeriod.endDate.afterStart"
      );
    }
    return undefined;
  })();

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      startDate: date ? date.format("YYYY-MM-DD") : "",
    }));
    markTouched("startDate");
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      endDate: date ? date.format("YYYY-MM-DD") : "",
    }));
    markTouched("endDate");
  };

  const renderUseCaseStep = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
      <Box sx={{ mb: 2, mt: 5 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: "20px",
            lineHeight: "28px",
            color: "#111827",
            textAlign: "center",
            fontWeight: 400,
          }}
        >
          {getLocalizedMessage(language, "createAppModal.screen1.title")}
        </Typography>
      </Box>
      <Grid
        container
        spacing={2.5}
        sx={{
          justifyContent: "center",
          flex: 1,
          alignItems: "stretch",
          mb: 0,
        }}
      >
        {useCaseOptions.map((option) => {
          const isSelected = formData.useCase === option.id;
          return (
            <Grid item key={option.id} xs={12} sm={6} md={3}>
              <ButtonBase
                disabled={option.disabled}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, useCase: option.id }))
                }
                sx={{
                  width: "100%",
                  height: "100%",
                  maxHeight: 150,
                  minWidth: 200,
                  maxWidth: 260,
                  borderRadius: 2,
                  border: isSelected
                    ? "3px solid #39bdf8"
                    : "1px solid #e5e7eb",
                  p: 1.5,
                  bgcolor: "#fff",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 0.75,
                  "&:hover": {
                    borderColor: isSelected ? "#39bdf8" : "#d1d5db",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    border: `1px dashed ${theme.palette.action.disabled}`,
                  },
                }}
              >
                <Box
                  sx={{
                    mt: 0.5,
                    mb: 0,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {option.icon}
                </Box>
                <Typography
                  fontWeight={600}
                  fontSize={15}
                  textAlign="center"
                  sx={{ color: "#111827", lineHeight: "22px" }}
                >
                  {getLocalizedMessage(language, option.title)}
                </Typography>
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>
      <Box
        sx={{
          width: "100%",
          height: 4,
          backgroundColor: "#e5e7eb",
          borderRadius: 2,
          overflow: "hidden",
          mt: 0,
          position: "relative",
          display: "flex",
        }}
      >
        {useCaseOptions.map((option) => (
          <Box
            key={option.id}
            sx={{
              flex: 1,
              height: "100%",
              backgroundColor:
                formData.useCase === option.id ? "#39bdf8" : "transparent",
              transition: "background-color 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          borderTop: `1px solid ${grey[200]}`,
          pt: 3,
          mt: 2,
          height: 100,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {formData.useCase && (
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              color: "#6b7280",
              margin: 0,
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: "#111827" }}>
              {getLocalizedMessage(
                language,
                useCaseMap[formData.useCase]?.title
              )}
            </Box>{" "}
            {getLocalizedMessage(
              language,
              useCaseMap[formData.useCase]?.description
            ).replace(/^.*?Use Case\s+enables\s+/i, "enables ")}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderNamingStep = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
      <Box sx={{ mb: 2, mt: 5 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: "20px",
            lineHeight: "28px",
            color: "#111827",
            textAlign: "center",
            fontWeight: 400,
          }}
        >
          {getLocalizedMessage(language, "createAppModal.screen2.title")}
        </Typography>
      </Box>
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}
      >
        <Grid item xs={12} sm={10} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={getLocalizedMessage(
                  language,
                  "createAppModal.screen2.programNameLabel"
                )}
                placeholder={getLocalizedMessage(
                  language,
                  "createAppModal.screen2.programNamePlaceholder"
                )}
                fullWidth
                value={formData.name}
                onChange={(event) => {
                  setFormData((prev) => ({
                    ...prev,
                    name: event.target.value,
                  }));
                  markTouched("name");
                  setNameAvailable(null);
                }}
                onBlur={() => {
                  markTouched("name");
                  void handleNameBlur();
                }}
                error={Boolean(nameError)}
                helperText={
                  nameError ??
                  (isCheckingName
                    ? getLocalizedMessage(
                        language,
                        "createAppModal.screen2.checkingAvailability"
                      )
                    : undefined)
                }
                disabled={isCreatingApp}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: nameError ? "#d32f2f !important" : "#e5e7eb",
                      borderRadius: "6px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#39bdf8",
                    },
                  },
                  "& input": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    opacity: 1,
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={getLocalizedMessage(
                  language,
                  "createAppModal.screen2.programOwnerLabel"
                )}
                placeholder={getLocalizedMessage(
                  language,
                  "createAppModal.screen2.programOwnerPlaceholder"
                )}
                fullWidth
                value={formData.programOwner}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    programOwner: event.target.value,
                  }))
                }
                onBlur={() => markTouched("programOwner")}
                inputProps={{ maxLength: PROGRAM_OWNER_MAX_LENGTH }}
                error={Boolean(programOwnerError)}
                helperText={programOwnerError}
                disabled={isCreatingApp}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: programOwnerError
                        ? "#d32f2f !important"
                        : "#e5e7eb",
                      borderRadius: "6px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#39bdf8",
                    },
                  },
                  "& input": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    opacity: 1,
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  const renderPeriodStep = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
      <Box sx={{ mb: 2, mt: 5 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: "20px",
            lineHeight: "28px",
            color: "#111827",
            textAlign: "center",
            fontWeight: 400,
          }}
        >
          {getLocalizedMessage(language, "createAppModal.screen3.title")}
        </Typography>
      </Box>
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}
      >
        <Grid item xs={12} sm={10} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box
                display="flex"
                sx={{ ".MuiFormControl-root": { width: "100%" } }}
              >
                <FormControl
                  sx={{ width: "100%" }}
                  error={Boolean(startDateError)}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={language}
                    localeText={
                      language === "en"
                        ? enUS.components.MuiLocalizationProvider.defaultProps
                            .localeText
                        : deDE.components.MuiLocalizationProvider.defaultProps
                            .localeText
                    }
                  >
                    <DatePicker
                      disabled={isCreatingApp}
                      value={
                        formData.startDate ? dayjs(formData.startDate) : null
                      }
                      onChange={handleStartDateChange}
                      label={getLocalizedMessage(
                        language,
                        "createAppModal.screen3.startDateLabel"
                      )}
                      PaperProps={{
                        sx: {
                          ".Mui-selected": {
                            color: "#ffffff !important",
                          },
                        },
                      }}
                      renderInput={(params: TextFieldProps) => (
                        <>
                          <TextField
                            {...params}
                            disabled={isCreatingApp}
                            sx={{
                              "& input": {
                                fontSize: "14px",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "14px",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#39bdf8",
                              },
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: "#d1d5db",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#39bdf8",
                                },
                              },
                              fieldset: {
                                borderRadius: "6px",
                                borderColor: startDateError
                                  ? "#d32f2f !important"
                                  : "#e5e7eb",
                              },
                            }}
                          />
                          {startDateError && (
                            <FormHelperText>{startDateError}</FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                display="flex"
                sx={{ ".MuiFormControl-root": { width: "100%" } }}
              >
                <FormControl
                  sx={{ width: "100%" }}
                  error={Boolean(endDateError)}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={language}
                    localeText={
                      language === "en"
                        ? enUS.components.MuiLocalizationProvider.defaultProps
                            .localeText
                        : deDE.components.MuiLocalizationProvider.defaultProps
                            .localeText
                    }
                  >
                    <DatePicker
                      disabled={isCreatingApp}
                      value={formData.endDate ? dayjs(formData.endDate) : null}
                      onChange={handleEndDateChange}
                      label={getLocalizedMessage(
                        language,
                        "createAppModal.screen3.endDateLabel"
                      )}
                      minDate={
                        formData.startDate
                          ? dayjs(formData.startDate)
                          : undefined
                      }
                      PaperProps={{
                        sx: {
                          ".Mui-selected": {
                            color: "#ffffff !important",
                          },
                        },
                      }}
                      renderInput={(params: TextFieldProps) => (
                        <>
                          <TextField
                            {...params}
                            disabled={isCreatingApp}
                            sx={{
                              "& input": {
                                fontSize: "14px",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "14px",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#39bdf8",
                              },
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: "#d1d5db",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#39bdf8",
                                },
                              },
                              fieldset: {
                                borderRadius: "6px",
                                borderColor: endDateError
                                  ? "#d32f2f !important"
                                  : "#e5e7eb",
                              },
                            }}
                          />
                          {endDateError && (
                            <FormHelperText>{endDateError}</FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  const renderFrequencyStep = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
      <Box sx={{ mb: 2, mt: 5 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: "20px",
            lineHeight: "28px",
            color: "#111827",
            textAlign: "center",
            fontWeight: 400,
          }}
        ></Typography>
      </Box>
      <Grid
        container
        spacing={3}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}
      >
        <Grid item xs={12} sm={10} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={3} justifyContent="center">
                {[
                  {
                    label: getLocalizedMessage(
                      language,
                      "createAppModal.screen4.weekly"
                    ),
                    value: "weekly",
                  },
                  {
                    label: getLocalizedMessage(
                      language,
                      "createAppModal.screen4.monthly"
                    ),
                    value: "monthly",
                  },
                  {
                    label: getLocalizedMessage(
                      language,
                      "createAppModal.screen4.yearly"
                    ),
                    value: "yearly",
                  },
                ].map((option) => (
                  <Grid item key={option.value}>
                    <FrequencyButton
                      label={option.label}
                      isActive={formData.frequencyUnit === option.value}
                      onClick={() =>
                        handleFrequencyUnitChange(option.value as FrequencyUnit)
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ minHeight: 200 }}>
                {formData.frequencyUnit === "weekly" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        gap={2}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.recurEvery"
                          )}
                        </Typography>
                        <TextField
                          type="number"
                          value={formData.frequencyInterval}
                          onChange={(event) =>
                            setFormData((prev) => ({
                              ...prev,
                              frequencyInterval: Number.isNaN(
                                Number(event.target.value)
                              )
                                ? 0
                                : Number(event.target.value),
                            }))
                          }
                          inputProps={{ min: 1 }}
                          sx={{ width: 120 }}
                        />
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.weeksOn"
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={2} justifyContent="center">
                        {dayOptions.map((day) => (
                          <Grid item key={day}>
                            <FrequencyButton
                              label={getLocalizedMessage(
                                language,
                                `createAppModal.screen4.${day.toLowerCase()}`
                              )}
                              isActive={formData.weeklyDay === day}
                              onClick={() => handleWeeklyDaySelect(day)}
                              width={120}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {formData.frequencyUnit === "monthly" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        gap={2}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.alwaysRecurs"
                          )}
                        </Typography>
                        <TextField
                          type="number"
                          value={formData.monthlyDay ?? ""}
                          onChange={(event) =>
                            setFormData((prev) => ({
                              ...prev,
                              monthlyDay: Number.isNaN(
                                Number(event.target.value)
                              )
                                ? undefined
                                : Number(event.target.value),
                            }))
                          }
                          inputProps={{ min: 1, max: 31 }}
                          sx={{ width: 120 }}
                        />
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.ofEvery"
                          )}
                        </Typography>
                        <TextField
                          type="number"
                          value={formData.frequencyInterval}
                          onChange={(event) =>
                            setFormData((prev) => ({
                              ...prev,
                              frequencyInterval: Number.isNaN(
                                Number(event.target.value)
                              )
                                ? 0
                                : Number(event.target.value),
                            }))
                          }
                          inputProps={{ min: 1 }}
                          sx={{ width: 120 }}
                        />
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.months"
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {formData.frequencyUnit === "yearly" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        gap={2}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.recurEvery"
                          )}
                        </Typography>
                        <TextField
                          type="number"
                          value={formData.frequencyInterval}
                          onChange={(event) =>
                            setFormData((prev) => ({
                              ...prev,
                              frequencyInterval: Number.isNaN(
                                Number(event.target.value)
                              )
                                ? 0
                                : Number(event.target.value),
                            }))
                          }
                          inputProps={{ min: 1 }}
                          sx={{ width: 120 }}
                        />
                        <Typography>
                          {getLocalizedMessage(
                            language,
                            "createAppModal.screen4.yearsOn"
                          )}
                        </Typography>
                        <TextField
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={formData.yearlyDate}
                          onChange={(event) =>
                            setFormData((prev) => ({
                              ...prev,
                              yearlyDate: event.target.value,
                            }))
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  const renderCurrentStep = () => {
    switch (activeStep) {
      case 0:
        return renderUseCaseStep();
      case 1:
        return renderNamingStep();
      case 2:
        return renderPeriodStep();
      case 3:
        return renderFrequencyStep();
      default:
        return null;
    }
  };

  const renderProgressBar = () => (
    <Box display="flex" gap={1} flexGrow={1} mx={3}>
      {steps.map((_, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 999,
            backgroundColor: index <= activeStep ? "#374151" : "#e5e7eb",
          }}
        />
      ))}
    </Box>
  );

  const renderNavigation = () => (
    <Box
      display="flex"
      alignItems="center"
      mt={5}
      flexWrap="wrap"
      gap={2}
      sx={{
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      <Button
        variant="text"
        onClick={() => {
          if (activeStep === 0) {
            handleClose();
          } else {
            setActiveStep((prev) => Math.max(prev - 1, 0));
          }
        }}
        disabled={isCreatingApp}
        sx={{
          textTransform: "none",
          color: "#374151",
          fontWeight: 500,
          minWidth: "auto",
          px: 2,
        }}
      >
        {getLocalizedMessage(language, "dialogActions.back")}
      </Button>
      {renderProgressBar()}
      <Button
        variant="contained"
        onClick={
          activeStep === steps.length - 1
            ? handleSubmit
            : () => setActiveStep((prev) => prev + 1)
        }
        disabled={!isCurrentStepValid || isCreatingApp}
        sx={{
          backgroundColor: "#374151",
          color: "#ffffff",
          textTransform: "none",
          fontWeight: 500,
          px: 3,
          py: 1,
          "&:hover": {
            backgroundColor: "#1f2937",
          },
          "&:disabled": {
            backgroundColor: "#d1d5db",
            color: "#9ca3af",
          },
        }}
      >
        {activeStep === steps.length - 1
          ? isCreatingApp
            ? getLocalizedMessage(language, "dialogActions.creating")
            : getLocalizedMessage(language, "dialogActions.finish")
          : getLocalizedMessage(language, "dialogActions.next")}
      </Button>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={isCreatingApp ? undefined : handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "fit-content",
          borderRadius: 4,
          maxWidth: 960,
          width: "100%",
          backgroundColor: "#ffffff",
          boxShadow: "none",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(147, 197, 253, 0.5)",
          background:
            "linear-gradient(135deg, rgba(147, 197, 253, 0.3) 0%, rgba(196, 181, 253, 0.3) 100%)",
        },
      }}
    >
      <DialogContent
        sx={{
          padding: "32px 40px !important",
          backgroundColor: "#ffffff",
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          disabled={isCreatingApp}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#6b7280",
            "&:hover": {
              color: "#111827",
              backgroundColor: "#f3f4f6",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        {isCreatingApp ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ color: "#666", fontWeight: 500 }}>
              {getLocalizedMessage(language, "createAppModal.creatingApp")}
            </Typography>
          </Box>
        ) : successMessage ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 3,
              gap: 2,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: "#4caf50",
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "scale(0)",
                    opacity: 0,
                  },
                  "50%": {
                    transform: "scale(1.1)",
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {successMessage}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                textAlign: "center",
                mt: 1,
              }}
            >
              {getLocalizedMessage(
                language,
                "createAppModal.successDescription"
              )}
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{
                bgcolor: "#ff8c00",
                color: "#fff",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#e67e00" },
              }}
            >
              {getLocalizedMessage(language, "dialogActions.close")}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {renderCurrentStep()}
            {renderNavigation()}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
